import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarSync, Link2, Unlink, RefreshCw, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loader } from "@/components/dashboard/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function StaffCalendar() {
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/google/calendar/status").then(({ data }) => setStatus(data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("gcal") === "ok") { toast.success("Google Agenda connecté"); setParams({}); load(); }
    if (params.get("gcal") === "error") { toast.error("Échec de la connexion Google Agenda"); setParams({}); }
  }, [params, setParams]);
  useEffect(() => { if (status?.connected) api.get("/google/calendar/calendars").then(({ data }) => setCalendars(data || [])).catch(() => {}); }, [status?.connected]);

  const connect = async () => {
    try { const { data } = await api.post("/google/calendar/connect", { redirect_base: window.location.origin }); window.location.href = data.authorization_url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const disconnect = async () => { await api.post("/google/calendar/disconnect"); toast.success("Google Agenda déconnecté"); load(); };
  const saveSettings = async (patch) => {
    const next = { ...status.settings, ...patch };
    setStatus((s) => ({ ...s, settings: next }));
    await api.put("/google/calendar/settings", { settings: next });
  };
  const setCalendar = async (id) => { setStatus((s) => ({ ...s, calendar_id: id })); await api.put("/google/calendar/settings", { calendar_id: id }); toast.success("Calendrier sélectionné"); };
  const syncNow = async () => { try { const { data } = await api.post("/google/calendar/sync-now"); toast.success(`${data.synced} mission(s) synchronisée(s)`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (status === null) return <Loader />;
  const S = status.settings || {};
  return (
    <div>
      <PageHeader title="Mon calendrier externe" subtitle="Connectez votre propre Google Agenda. Vous seul pouvez l'autoriser." />
      <div className="mb-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3 max-w-2xl">
        <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
        Votre connexion Google vous appartient. Les administrateurs voient uniquement vos disponibilités opérationnelles — jamais le contenu de vos événements privés.
      </div>

      {!status.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-800 max-w-2xl">
          La synchronisation Google Agenda n'est pas encore activée. L'administrateur système doit d'abord configurer les identifiants Google Cloud. La fonctionnalité s'activera automatiquement ensuite.
        </div>
      )}

      {status.configured && !status.connected && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
          <CalendarSync className="h-8 w-8 text-primary" />
          <h3 className="mt-3 font-heading font-semibold text-lg text-slate-900">Connecter Google Agenda</h3>
          <p className="mt-1 text-slate-500">Vos missions confirmées seront ajoutées à votre agenda, et vos périodes occupées pourront éviter les conflits.</p>
          <Button className="mt-5 bg-primary" onClick={connect} data-testid="gcal-connect"><Link2 className="h-4 w-4 mr-1.5" />Connecter Google Agenda</Button>
        </div>
      )}

      {status.configured && status.connected && (
        <div className="max-w-2xl space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-medium text-slate-900">Connecté</p><p className="text-sm text-slate-500">{status.email}</p>{status.last_sync && <p className="text-xs text-slate-400 mt-0.5">Dernière synchronisation : {new Date(status.last_sync).toLocaleString("fr-CH")}</p>}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={syncNow} data-testid="gcal-sync"><RefreshCw className="h-4 w-4 mr-1.5" />Synchroniser maintenant</Button>
              <Button variant="ghost" size="sm" className="text-rose-600" onClick={disconnect} data-testid="gcal-disconnect"><Unlink className="h-4 w-4 mr-1.5" />Déconnecter</Button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Calendrier utilisé pour les missions</h3>
            <Select value={status.calendar_id} onValueChange={setCalendar}><SelectTrigger className="w-full sm:w-80"><SelectValue placeholder="primary" /></SelectTrigger>
              <SelectContent>{calendars.map((c) => <SelectItem key={c.id} value={c.id}>{c.summary}{c.primary ? " (principal)" : ""}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <h3 className="font-semibold text-slate-900">Options de synchronisation</h3>
            <label className="flex items-center justify-between"><span className="text-sm">Ajouter mes missions confirmées dans Google Agenda</span><Switch checked={S.push_missions !== false} onCheckedChange={(v) => saveSettings({ push_missions: v })} /></label>
            <label className="flex items-center justify-between"><span className="text-sm">Mettre à jour automatiquement les missions modifiées</span><Switch checked={S.update_missions !== false} onCheckedChange={(v) => saveSettings({ update_missions: v })} /></label>
            <label className="flex items-center justify-between"><span className="text-sm">Supprimer dans Google Agenda les missions annulées</span><Switch checked={S.cancel_missions !== false} onCheckedChange={(v) => saveSettings({ cancel_missions: v })} /></label>
            <label className="flex items-center justify-between pt-3 border-t border-slate-100"><span className="text-sm">Utiliser mes périodes occupées Google pour éviter les conflits</span><Switch checked={!!S.use_busy} onCheckedChange={(v) => saveSettings({ use_busy: v })} data-testid="gcal-usebusy" /></label>
          </div>
        </div>
      )}
    </div>
  );
}
